import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import Seo from "@/components/Seo";
import { useCart } from "@/context/CartContext";
import { ProductCard } from "@/components/ProductCard";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

export default function ProductPage() {
  const { id } = useParams();
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [activeImage, setActiveImage] = useState(0);
  const [size, setSize] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setProduct(null);
    setSize(null);
    setActiveImage(0);
    api.get(`/products/${id}`)
      .then(({ data }) => {
        setProduct(data);
        if ((data.sizes || []).length === 1) setSize(data.sizes[0].size);
        api.get("/products").then(({ data: all }) => {
          setRelated(all.filter((p) => p.id !== data.id && p.category === data.category).slice(0, 4));
        });
      })
      .catch(() => setNotFound(true));
  }, [id]);

  const images = useMemo(() => {
    if (!product) return [];
    return [product.main_image, product.model_image, product.flatlay_image].filter(Boolean);
  }, [product]);

  if (notFound) {
    return (
      <main data-testid="product-not-found" className="pt-40 pb-32 text-center">
        <h1 className="font-display text-4xl font-extrabold uppercase tracking-tighter">Tee not found</h1>
        <Link to="/shop" className="mt-6 inline-block font-mono text-xs uppercase tracking-widest text-[#4D7C0F]">
          Back to the archive
        </Link>
      </main>
    );
  }

  if (!product) {
    return (
      <main data-testid="product-loading" className="pt-40 pb-32 text-center font-mono text-xs uppercase tracking-widest text-zinc-500">
        Loading…
      </main>
    );
  }

  const selected = product.sizes?.find((s) => s.size === size);
  const totalStock = (product.sizes || []).reduce((n, s) => n + s.stock, 0);

  const add = () => {
    if (!size) {
      toast.error("Pick a size first");
      return;
    }
    addItem(product, size);
    toast.success(`${product.name} — ${size} added to cart`);
  };

  return (
    <main data-testid="product-page" className="pt-16">
      <Seo
        title={`${product.name} — Marabou Design`}
        description={product.description}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-10">
        <Link
          to="/shop"
          data-testid="product-back-link"
          className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-zinc-500 hover:text-[#4D7C0F] transition-colors"
        >
          <ArrowLeft size={14} /> Back to shop
        </Link>

        <div className="mt-8 grid gap-12 lg:grid-cols-2">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6 }}>
            <div className="relative overflow-hidden border border-[#E4E4DE]">
              <img
                data-testid="product-main-image"
                src={images[activeImage]}
                alt={product.name}
                className="w-full aspect-[3/4] object-cover"
              />
              {product.tag && (
                <span className="absolute top-3 left-3 bg-[#C8FF1E] px-2 py-1 font-mono text-[10px] font-bold uppercase tracking-widest text-black">
                  {product.tag}
                </span>
              )}
            </div>
            {images.length > 1 && (
              <div className="mt-4 flex gap-3">
                {images.map((img, i) => (
                  <button
                    key={i}
                    data-testid={`product-thumb-${i}`}
                    onClick={() => setActiveImage(i)}
                    className={`h-20 w-16 overflow-hidden border transition-colors ${
                      activeImage === i ? "border-[#C8FF1E]" : "border-[#E4E4DE] hover:border-zinc-500"
                    }`}
                  >
                    <img src={img} alt="" className="h-full w-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#4D7C0F]/90">
              {product.category} · {product.gsm}
            </p>
            <h1 data-testid="product-name" className="mt-3 font-display text-3xl sm:text-5xl font-extrabold uppercase tracking-tighter leading-[0.95]">
              {product.name}
            </h1>
            <p className="mt-3 font-mono text-xs uppercase tracking-widest text-zinc-500">{product.color}</p>
            <p data-testid="product-price" className="mt-5 font-mono text-2xl font-bold text-[#4D7C0F]">
              ${product.price}
            </p>
            <p data-testid="product-description" className="mt-6 text-sm sm:text-base text-zinc-500 leading-relaxed">
              {product.description}
            </p>

            <div className="mt-8">
              <div className="flex items-center justify-between">
                <p className="font-mono text-xs uppercase tracking-[0.25em] text-zinc-500">Select size</p>
                {totalStock > 0 && totalStock <= 15 && (
                  <p data-testid="low-stock-badge" className="font-mono text-[10px] uppercase tracking-widest text-orange-500">
                    Low stock — {totalStock} left
                  </p>
                )}
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                {(product.sizes || []).map((s) => (
                  <button
                    key={s.size}
                    data-testid={`size-select-${s.size.toLowerCase()}`}
                    disabled={s.stock <= 0}
                    onClick={() => setSize(s.size)}
                    className={`h-12 min-w-14 px-3 border font-mono text-sm transition-colors ${
                      size === s.size
                        ? "border-[#C8FF1E] bg-[#C8FF1E] text-black font-bold"
                        : "border-[#E4E4DE] text-zinc-700 hover:border-zinc-500 disabled:opacity-30 disabled:line-through"
                    }`}
                  >
                    {s.size}
                  </button>
                ))}
              </div>
              {selected && (
                <p data-testid="size-stock-count" className="mt-2 font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                  {selected.stock} in stock for size {selected.size}
                </p>
              )}
            </div>

            <button
              data-testid="product-add-to-cart-btn"
              onClick={add}
              disabled={totalStock <= 0}
              className="mt-8 w-full bg-[#C8FF1E] py-4 font-mono text-xs font-bold uppercase tracking-[0.2em] text-black hover:bg-[#B0E600] disabled:opacity-40 transition-colors"
            >
              {totalStock <= 0 ? "Sold out" : "Add to cart"}
            </button>

            <div className="mt-8">
              <Accordion type="single" collapsible className="border-t border-[#E4E4DE]">
                <AccordionItem value="details" className="border-[#E4E4DE]">
                  <AccordionTrigger data-testid="accordion-details" className="font-mono text-xs uppercase tracking-widest text-zinc-600 hover:text-[#4D7C0F]">
                    Details & fit
                  </AccordionTrigger>
                  <AccordionContent className="text-sm text-zinc-500 leading-relaxed">
                    Boxy architectural cut with dropped shoulders. {product.gsm} ring-spun
                    organic cotton, pre-shrunk. Take your usual size for the intended oversized fit,
                    or size down for a regular fit.
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="care" className="border-[#E4E4DE]">
                  <AccordionTrigger data-testid="accordion-care" className="font-mono text-xs uppercase tracking-widest text-zinc-600 hover:text-[#4D7C0F]">
                    Care
                  </AccordionTrigger>
                  <AccordionContent className="text-sm text-zinc-500 leading-relaxed">
                    Cold machine wash inside-out, hang dry. Water-based archival inks are
                    designed to fade and crack gradually — that's the point.
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="shipping" className="border-[#E4E4DE]">
                  <AccordionTrigger data-testid="accordion-shipping" className="font-mono text-xs uppercase tracking-widest text-zinc-600 hover:text-[#4D7C0F]">
                    Shipping & returns
                  </AccordionTrigger>
                  <AccordionContent className="text-sm text-zinc-500 leading-relaxed">
                    Ships worldwide within 48h. Free shipping on orders over $150.
                    30-day returns on unworn garments.
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
          </motion.div>
        </div>

        {related.length > 0 && (
          <section data-testid="related-products" className="mt-24">
            <h2 className="font-display text-2xl sm:text-3xl font-bold uppercase tracking-tight">
              Complete the fit
            </h2>
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
              {related.map((p, i) => (
                <ProductCard key={p.id} product={p} index={i} />
              ))}
            </div>
          </section>
        )}
      </div>
    </main>
  );
}
